import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  TextField,
  Box,
  IconButton,
  Typography,
  Button,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { placeholderText } from "../../constants/colors";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DropdownIcon } from "../../assets/Vector 474 (Stroke).svg";
import CommonSelect from "../common/CommonSelect";
import { CustomCheckbox } from "../opportunities/OpportunitiesTable";
import CircularProgress from "@mui/material/CircularProgress";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: 0,
    width: "700px",
    [theme.breakpoints.down("md")]: {
      width: "95%",
      maxWidth: "100%",
    },
    minHeight: "400px",
    maxHeight: "70vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#F7F8FB",
    margin: "auto",
  },
  "& .MuiDialogContent-root": {
    padding: 0,
    backgroundColor: "#F7F8FB",
    flex: 1,
    overflowY: "auto",
  },
}));

const ModalHeader = styled(Box)(() => ({
  backgroundColor: "#F7F8FB",
  padding: "16px 15px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderRadius: "12px 12px 0 0",
}));

const ModalContent = styled(Box)(() => ({
  padding: "0 15px 0 15px",
  backgroundColor: "#F7F8FB",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  "& > div": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: "24px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    marginBottom: "2px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minHeight: "250px",
  },
}));

const ModalFooter = styled(Box)(() => ({
  padding: "8px 24px 16px",
  display: "flex",
  justifyContent: "flex-end",
  gap: "16px",
  backgroundColor: "#F7F8FB",
  borderRadius: "0 0 12px 12px",
  "& .MuiButton-root": {
    minWidth: "140px",
    padding: "8px 16px",
    textTransform: "none",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#403C6F",
  marginBottom: "8px",
}));

const StyledButton = styled(Button)({
  textTransform: "none",
  padding: "8px 24px",
  borderRadius: "20px",
  fontWeight: 500,
  fontSize: "0.875rem",
  lineHeight: 1.71,
  letterSpacing: "0.4px",
  boxShadow: "none",
  width: "68px",
  height: "38px",
  "&.MuiButton-outlined": {
    borderColor: "#9F9DB6",
    color: "#403C6F",
    backgroundColor: "#FFFFFF",
    "&:hover": {
      backgroundColor: "transparent",
      borderColor: "#9F9DB6",
    },
  },
  "&.MuiButton-contained": {
    backgroundColor: "#4b3f72",
    color: "white",
    "&:hover": {
      backgroundColor: "#4b3f72",
    },
  },
});

function getRecipientType(task) {
  return task?.recipient?.type?.toUpperCase?.() || "";
}
function getOutreachReasons(recipientType) {
  if (recipientType === "MEMBER" || recipientType === "OTHERS") {
    return ["Phone Call", "Email", "SMS"];
  }
  if (recipientType === "PROVIDER" || recipientType === "PHARMACY") {
    return ["Phone Call", "Email", "Fax"];
  }
  return [];
}
function getOutreachOutcomes(recipientType) {
  if (recipientType === "MEMBER" || recipientType === "OTHERS") {
    return [
      "Reached",
      "Not Reached - Left Message",
      "Not Reached - No Message",
      "Scheduled Callback",
      "Do Not Call",
    ];
  }
  if (recipientType === "PROVIDER" || recipientType === "PHARMACY") {
    return [
      "Reached",
      "Not Reached - Left Message",
      "Not Reached - No Message",
    ];
  }
  return [];
}
function getStatusOptions(recipientType) {
  if (recipientType === "MEMBER") {
    return ["Enrolled", "Declined", "Eligible", "Resolved", "Not Resolved"];
  }
  if (["OTHERS", "PROVIDER", "PHARMACY"].includes(recipientType)) {
    return ["Resolved", "Not Resolved", "Declined"];
  }
  return [];
}

const CompleteTaskModal = ({ open, onClose, onSave, task }) => {
  const [formData, setFormData] = useState({
    date: new Date(),
    outreachReason: "",
    outreachOutcome: "",
    status: "",
    notes: "",
  });
  const [isMarkAsComplete, setIsMarkAsComplete] = useState(false);
  const [anchorEl, setAnchorEl] = useState({
    outreachReason: null,
    outreachOutcome: null,
    status: null,
  });
  const filterButtonRefs = {
    outreachReason: React.useRef(null),
    outreachOutcome: React.useRef(null),
    status: React.useRef(null),
  };
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use helpers for recipient type and dropdown options
  const recipientType = getRecipientType(task);
  // (isMember, isOther, isProvider, isPharmacy) are not needed anymore
  const outreachReasons = getOutreachReasons(recipientType);
  const outreachOutcomes = getOutreachOutcomes(recipientType);
  const statusOptions = getStatusOptions(recipientType);

  const handleTypeToggle = (field, value) => {
    handleChange(field)({ target: { value } });
    setAnchorEl((prev) => ({ ...prev, [field]: null }));
  };
  const handleOpenSelect = (field, event) => {
    setAnchorEl((prev) => ({ ...prev, [field]: event.currentTarget }));
  };
  const handleCloseSelect = (field) => {
    setAnchorEl((prev) => ({ ...prev, [field]: null }));
  };
  const renderDropdownButton = (field, value, options, placeholder) => {
    const hasError = !!errors[field];
    // Options are strings, so find the matching option
    const selectedLabel =
      Array.isArray(options) && value
        ? options.find((opt) => opt === value) || value
        : value || "";
    const displayText = value ? selectedLabel : placeholder;
    return (
      <>
        <Button
          ref={filterButtonRefs[field]}
          onClick={(e) => handleOpenSelect(field, e)}
          aria-label={displayText}
          aria-haspopup="listbox"
          aria-expanded={Boolean(anchorEl[field])}
          sx={{
            width: "100%",
            minWidth: "200px",
            height: "40px",
            border: hasError ? "1px solid #D32F2F" : "1px solid #E9E9E8",
            borderRadius: "4px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 12px",
            backgroundColor: "#FFFFFF",
            textTransform: "none",
            color: value ? "#000000" : placeholderText,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: "#FFFFFF",
              cursor: "pointer",
              borderColor: hasError ? "#D32F2F" : "#4B3F72",
            },
            "&:focus-within": {
              borderColor: hasError ? "#D32F2F" : "#4B3F72",
              boxShadow: hasError ? "0 0 0 1px #D32F2F" : "0 0 0 1px #4B3F72",
            },
            "&:focus-visible": {
              outline: "2px solid #4B3F72",
              outlineOffset: "2px",
            },
            transition: "border-color 0.2s, box-shadow 0.2s",
          }}
        >
          <span
            style={{
              fontWeight: value ? "normal" : "400",
              color: (() => {
                if (value) {
                  return hasError ? "#D32F2F" : "inherit";
                } else {
                  return placeholderText;
                }
              })(),
              opacity: value ? 1 : 0.8,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {displayText}
          </span>
          <DropdownIcon
            style={{
              marginLeft: "8px",
              color: hasError ? "#D32F2F" : "#4B3F72",
            }}
          />
        </Button>
        {hasError && (
          <Typography
            variant="caption"
            sx={{
              color: "#D32F2F",
              fontSize: "0.75rem",
              marginTop: "4px",
              display: "block",
              lineHeight: "1.2",
            }}
          >
            {errors[field]}
          </Typography>
        )}
      </>
    );
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.date) newErrors.date = "Please select a date";
    if (!formData.outreachReason)
      newErrors.outreachReason = "Please select an outreach method";
    if (!formData.outreachOutcome)
      newErrors.outreachOutcome = "Please select an outreach outcome";
    if (!formData.status) newErrors.status = "Please select a status";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getModalTitle = React.useCallback(() => {
    if (!task?.recipient) {
      return "Task Completion";
    }
    const recipientType = task.recipient.type || "";
    const isMemberOrOthers =
      recipientType === "MEMBER" || recipientType === "OTHERS";
    return isMemberOrOthers ? "Add Disposition" : "Task Completion";
  }, [task]);

  const handleCheckboxChange = (event) => {
    setIsMarkAsComplete(event.target.checked);
  };

  const handleSave = async () => {
    if (validateForm() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        const completeTaskData = {
          ...formData,
          date:
            formData.date instanceof Date
              ? formData.date.toISOString()
              : formData.date,
        };
        await onSave(completeTaskData, isMarkAsComplete);
        onClose();
      } catch (error) {
        console.error("Error saving task:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  React.useEffect(() => {
    if (open) {
      setFormData({
        date: new Date(),
        outreachReason: "",
        outreachOutcome: "",
        status: "",
        notes: "",
      });

      const isTaskCompletion = getModalTitle() === "Task Completion";
      setIsMarkAsComplete(isTaskCompletion);
      setErrors({});
    }
  }, [open, getModalTitle]);

  const getButtonContent = () => {
    if (isSubmitting) {
      const submittingText =
        getModalTitle() === "Task Completion"
          ? "Completing..."
          : "Submitting...";
      return (
        <>
          {submittingText}
          <CircularProgress size={20} aria-label="Processing" />
        </>
      );
    } else {
      return getModalTitle() === "Task Completion"
        ? "Complete Task"
        : "Submit";
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="complete-task-dialog-title"
      ModalProps={{
        keepMounted: false,
        disableEnforceFocus: false,
        disableAutoFocus: false,
        disableRestoreFocus: false,
      }}
    >
      <ModalHeader>
        <Typography
          id="complete-task-dialog-title"
          variant="h6"
          sx={{
            color: "#10100F",
            fontWeight: 600,
            fontSize: "18px",
            lineHeight: "32px",
          }}
        >
          {getModalTitle()}
        </Typography>
        <IconButton
          aria-label={`Close ${getModalTitle().toLowerCase()} dialog`}
          onClick={onClose}
          sx={{
            color: "#4B4F50",
            "&:hover": {
              backgroundColor: "rgba(75, 79, 80, 0.08)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </ModalHeader>

      <ModalContent>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            borderColor: "#E9E9E8",
          }}
        >
          {task?.recipient &&
            (task.recipient.type === "MEMBER" ||
              task.recipient.type === "OTHERS") && (
              <Box
                sx={{
                  width: { xs: "100%", sm: "320px" },
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "-4px",
                }}
              >
                <CustomCheckbox
                  checked={isMarkAsComplete}
                  onChange={handleCheckboxChange}
                  sx={{
                    transform: "scale(0.8)",
                    padding: "4px",
                    marginLeft: "-4px",
                  }}
                  slotProps={{ input: { "aria-label": "Mark task as complete" } }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#403C6F",
                    lineHeight: "16px",
                    marginLeft: "-4px",
                  }}
                >
                  Mark as complete
                </span>
              </Box>
            )}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 5 },
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1, width: "100%" }}>
              <FieldLabel>Date</FieldLabel>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  value={formData.date}
                  onChange={(newValue) =>
                    setFormData({ ...formData, date: newValue })
                  }
                  minDate={new Date()}
                  disabled
                  slots={{ openPickerIcon: null }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      disabled
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: null,
                          sx: {
                            height: "40px",
                            "& input": {
                              padding: "8px 14px",
                              color: formData.dueDate ? "#111827" : "#6B7280",
                              "&::placeholder": {
                                color: "#6B7280",
                                opacity: 1,
                              },
                            },
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#E9E9E8",
                          },
                          "&:hover fieldset": {
                            borderColor: "#E9E9E8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#E9E9E8",
                          },
                        },
                      }}
                    />
                  )}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      disabled: true,
                      slotProps: {
                        input: {
                          sx: {
                            height: "40px",
                            "& input": {
                              padding: "8px 14px",
                              color: formData.dueDate ? "#111827" : "#6B7280",
                              "&::placeholder": {
                                color: "#6B7280",
                                opacity: 1,
                              },
                            },
                          },
                          endAdornment: null,
                        },
                      },
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#E9E9E8",
                          },
                          "&:hover fieldset": {
                            borderColor: "#E9E9E8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#E9E9E8",
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: 1, width: "100%" }}>
              <FieldLabel required>Outreach Method</FieldLabel>
              <FormControl fullWidth error={!!errors.outreachReason}>
                {renderDropdownButton(
                  "outreachReason",
                  formData.outreachReason,
                  outreachReasons,
                  "Select a method"
                )}
                <CommonSelect
                  id="outreach-reason-select"
                  open={Boolean(anchorEl.outreachReason)}
                  anchorEl={anchorEl.outreachReason}
                  onClose={() => handleCloseSelect("outreachReason")}
                  inQuickview={false}
                  filterButtonRef={filterButtonRefs.outreachReason}
                  noteTypes={outreachReasons}
                  selectedTypes={
                    formData.outreachReason ? [formData.outreachReason] : []
                  }
                  handleTypeToggle={(type) =>
                    handleTypeToggle("outreachReason", type)
                  }
                  multiple={false}
                  width={
                    filterButtonRefs.outreachReason.current
                      ? `${filterButtonRefs.outreachReason.current.offsetWidth}px`
                      : "auto"
                  }
                />
              </FormControl>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 5 },
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1, width: "100%" }}>
              <FieldLabel required>Outreach Outcome</FieldLabel>
              <FormControl fullWidth error={!!errors.outreachOutcome}>
                {renderDropdownButton(
                  "outreachOutcome",
                  formData.outreachOutcome,
                  outreachOutcomes,
                  "Select an outcome"
                )}
                <CommonSelect
                  id="outreach-outcome-select"
                  open={Boolean(anchorEl.outreachOutcome)}
                  anchorEl={anchorEl.outreachOutcome}
                  onClose={() => handleCloseSelect("outreachOutcome")}
                  inQuickview={false}
                  filterButtonRef={filterButtonRefs.outreachOutcome}
                  noteTypes={outreachOutcomes}
                  selectedTypes={
                    formData.outreachOutcome ? [formData.outreachOutcome] : []
                  }
                  handleTypeToggle={(type) =>
                    handleTypeToggle("outreachOutcome", type)
                  }
                  multiple={false}
                  width={
                    filterButtonRefs.outreachOutcome.current
                      ? `${filterButtonRefs.outreachOutcome.current.offsetWidth}px`
                      : "auto"
                  }
                />
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, width: "100%" }}>
              <FieldLabel required>Status</FieldLabel>
              <FormControl fullWidth error={!!errors.status}>
                {renderDropdownButton(
                  "status",
                  formData.status,
                  statusOptions,
                  "Select status"
                )}
                <CommonSelect
                  id="status-select"
                  open={Boolean(anchorEl.status)}
                  anchorEl={anchorEl.status}
                  onClose={() => handleCloseSelect("status")}
                  inQuickview={false}
                  filterButtonRef={filterButtonRefs.status}
                  noteTypes={statusOptions}
                  selectedTypes={formData.status ? [formData.status] : []}
                  handleTypeToggle={(type) => handleTypeToggle("status", type)}
                  multiple={false}
                  width={
                    filterButtonRefs.status.current
                      ? `${filterButtonRefs.status.current.offsetWidth}px`
                      : "auto"
                  }
                />
              </FormControl>
            </Box>
          </Box>
        </div>
      </ModalContent>

      <ModalFooter>
        <StyledButton
          variant="outlined"
          onClick={onClose}
          sx={{
            minWidth: "80px",
            "&.MuiButton-root": {
              minWidth: "100px",
              width: "80px",
              padding: "8px 12px",
            },
          }}
        >
          Cancel
        </StyledButton>
        <StyledButton
          variant="contained"
          onClick={handleSave}
          disabled={isSubmitting}
          sx={{
            "&:disabled": {
              backgroundColor: "rgba(64, 60, 111, 0.5)",
              color: "white",
              cursor: "not-allowed",
            },
            minWidth: "140px",
            position: "relative",
            "& .MuiCircularProgress-root": {
              color: "white",
              position: "absolute",
              right: "12px",
              width: "20px !important",
              height: "20px !important",
            },
          }}
        >
          {getButtonContent()}
        </StyledButton>
      </ModalFooter>
    </StyledDialog>
  );
};


CompleteTaskModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  task: PropTypes.object,
};

export default CompleteTaskModal;
